import { Component, computed, inject, OnInit, Signal } from "@angular/core";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { injectQuery } from "@tanstack/angular-query-experimental";
import { MenuItem } from "primeng/api";

import { FeatureService, PermissionsService } from "@intocare/auth-data";
import { SharedCommunicationDataService } from "@intocare/data-access/shared/communication";
import { ContactComponent, NavService, UIModule } from "@intocare/ui";
import { ActivityBasedPollService, CONTEXT_TOKEN } from "@intocare/util";

/**
 * Inject the menu items, activating the notification counts as needed.
 *
 * This way of working will prevent unnecessary injections/subscriptions, as we only trigger the injection function for
 * menu items that will be active, after filtering them out based on the permissions and feature flags. These check only
 * happen once, because content must be loaded.
 *
 * TODO: This could be further optimized and turned into some utility to easily create menu items. Some menu items might
 * stand on their own functional-wise. These can thus be defined/configured in their own file/module/thing, and then
 * easily injected where needed, triggering subscriptions. In a way, each such menu item is its own
 * view-model/data-store, and be done with an injectable. This is exactly why Angular has multi-providers, so you could
 * then have every module that gets loaded provide their menu items, which then get injected into the menu based on
 * their config. Not that we have to do this, as explicit config has its own benefits, but food for thought. Another way
 * we can go about this is through components, which can then be placed into the template. If a component gets rendered
 * depends on the component itself, and for things as `hasNotifications`, a `viewChildren` query can be used for the
 * calculation.
 */
function injectSideMenu(
	items: (Omit<MenuItem, "notifications"> & { injectNotificationCount?: () => Signal<number> })[],
) {
	const permissionsService = inject(PermissionsService);
	const featureService = inject(FeatureService);

	return items
		.filter(
			(item) =>
				permissionsService.userHasPermissions(item.permissions || [])
				&& featureService.hasFeatures(item.features || []),
		)
		.map(({ injectNotificationCount, ...item }) => ({
			...item,
			...(injectNotificationCount && { notificationCount: injectNotificationCount() }),
			...(item.items ? { items: injectSideMenu(item.items) } : undefined),
		}));
}

@Component({
	imports: [RouterModule, TranslateModule, UIModule, ContactComponent],
	templateUrl: "home.component.html",
	styleUrls: ["home.component.scss"],
})
export class HomeComponent implements OnInit {
	private readonly navService = inject(NavService);
	private readonly contextService = inject(CONTEXT_TOKEN);
	private readonly activityBasedPollService = inject(ActivityBasedPollService);
	private readonly sharedCommunicationDataService = inject(SharedCommunicationDataService);

	private readonly _messagesPoll = this.activityBasedPollService.register(() =>
		this.sharedCommunicationDataService.invalidateMessagesState(),
	);

	protected readonly supportInfo = computed(() => this.contextService.context().supportInfo);

	private readonly basePathAccessRights = "access-rights/";
	private readonly basePathOrganisation = "organisation/";
	private readonly basePathCommunication = "communication/";

	protected readonly sideMenu = injectSideMenu([
		{
			label: "general.titles.dashboard",
			icon: "fa fa-bar-chart",
			routerLink: "dashboard",
			automationId: "dashboard",
			permissions: ["HQ.DASHBOARD"],
		},
		{
			label: "general.titles.careTree",
			icon: "fa fa-share-alt-square",
			routerLink: this.basePathOrganisation + "caretree",
			automationId: "tab-caretree",
			permissions: ["HQ.CARE_TREE"],
		},
		{
			label: "general.titles.communication",
			icon: "fa fa-envelope",
			automationId: "tab-communication",
			permissions: ["HQ.INTERNAL_COMMUNICATION"],
			injectNotificationCount: () => {
				const sharedCommunicationDataService = inject(SharedCommunicationDataService);
				const messagesStateQuery = injectQuery(() => sharedCommunicationDataService.messagesStateQueryOptions());
				return computed(() => messagesStateQuery.data()?.data?.messages?.unread?.count ?? 0);
			},
			items: [
				{
					label: "general.titles.receivedMessages",
					routerLink: this.basePathCommunication + "internal-communication/received",
					automationId: "tab-received-messages",
					injectNotificationCount: () => {
						const sharedCommunicationDataService = inject(SharedCommunicationDataService);
						const messagesStateQuery = injectQuery(() => sharedCommunicationDataService.messagesStateQueryOptions());
						return computed(() => messagesStateQuery.data()?.data?.messages?.unread?.count ?? 0);
					},
				},
				{
					label: "general.titles.sentMessages",
					routerLink: this.basePathCommunication + "internal-communication/sent",
					automationId: "tab-sent-messages",
				},
			],
		},
		{
			label: "general.titles.calendarActivities",
			icon: "fa fae-halter",
			routerLink: this.basePathOrganisation + "activities",
			automationId: "tab-activities",
			permissions: ["HQ.ACTIVITIES"],
		},
		{
			label: "general.titles.alarms",
			icon: "fa fa-bell-o",
			routerLink: this.basePathOrganisation + "alarms",
			automationId: "tab-alarms",
			permissions: ["HQ.ALARMS"],
		},
		{
			label: "general.titles.documents",
			icon: "fa fa-file-text-o",
			routerLink: this.basePathOrganisation + "documents",
			automationId: "tab-documents",
			permissions: ["HQ.DOCUMENT_TEMPLATES"],
		},
		{
			label: "general.titles.woundCare",
			icon: "fa fa-medkit",
			automationId: "tab-woundcare",
			permissions: ["HQ.WOUNDCARE"],
			items: [
				{
					label: "general.titles.woundCareProtocols",
					routerLink: this.basePathOrganisation + "wound-care/protocols",
					automationId: "tab-woundcare-protocols",
				},
				{
					label: "general.titles.woundCareTypes",
					routerLink: this.basePathOrganisation + "wound-care/wound-types",
					automationId: "tab-wound-types",
				},
			],
		},
		{
			label: "general.titles.pharmacies",
			icon: "fa fae-medicine",
			automationId: "tab-medication",
			permissions: { $or: ["HQ.PHARMACY", "HQ.PHARMACY_LINKS"] },
			items: [
				{
					label: "general.titles.formularium",
					routerLink: this.basePathOrganisation + "medication",
					automationId: "tab-formularium",
					permissions: ["HQ.PHARMACY"],
				},
				{
					label: "general.titles.pharmacy-links",
					routerLink: this.basePathOrganisation + "pharmacy-links",
					automationId: "tab-pharmacy-links",
					permissions: ["HQ.PHARMACY_LINKS"],
				},
			],
		},
		{
			label: "access.menu.user.header",
			icon: "fa fa-users",
			automationId: "tab-users",
			permissions: ["HQ.USER_MANAGEMENT"],
			items: [
				{
					label: "access.menu.user.view.perUser",
					automationId: "user-view-per-user",
					routerLink: this.basePathAccessRights + "users/manage/by-user",
				},
				{
					label: "access.menu.user.view.perRole",
					automationId: "user-view-per-role",
					routerLink: this.basePathAccessRights + "users/manage/by-role",
				},
				{
					label: "access.menu.user.view.perCareFacility",
					automationId: "user-view-per-care-facility",
					routerLink: this.basePathAccessRights + "users/manage/by-care-facility",
				},
				{
					label: "access.menu.password.header",
					automationId: "password-policy-view",
					routerLink: this.basePathAccessRights + "password-policy",
				},
				{
					label: "access.menu.communicationGroups.header",
					automationId: "communication-groups",
					routerLink: this.basePathAccessRights + "communication-groups",
					permissions: ["HQ.COMMUNICATION_GROUPS"],
				},
			],
		},
		{
			label: "access.menu.access.header",
			icon: "fa fa-lock",
			automationId: "tab-access",
			permissions: ["HQ.ACCESS_MANAGEMENT"],
			items: [
				{
					label: "access.menu.access.view.roles",
					automationId: "access-view-roles",
					routerLink: this.basePathAccessRights + "roles",
				},
				{
					label: "access.menu.access.view.medicationGroups",
					automationId: "access-view-medication-groups",
					routerLink: this.basePathAccessRights + "medication-group-roles",
				},
			],
		},
		{
			label: "general.titles.configuration",
			icon: "fa fa-sliders",
			automationId: "tab-configuration",
			permissions: {
				$or: ["HQ.CONFIGURATION", "HQ.ORGANISING_AUTHORITIES", "HQ.DEPARTMENTS_AND_ROOMS"],
			},
			items: [
				{
					label: "general.titles.HQDetails",
					automationId: "tab-hq-config",
					routerLink: [this.basePathOrganisation + "configuration", "hq"],
					permissions: ["HQ.CONFIGURATION"],
				},
				{
					label: "general.titles.organisingAuthorities",
					automationId: "tab-organising-authority",
					routerLink: [this.basePathOrganisation + "configuration", "organising-authorities"],
					permissions: ["HQ.ORGANISING_AUTHORITIES"],
				},
				{
					label: "general.titles.departmentsAndRooms",
					automationId: "tab-departments-and-rooms",
					routerLink: [this.basePathOrganisation + "configuration", "departments-and-rooms"],
					permissions: ["HQ.DEPARTMENTS_AND_ROOMS"],
				},
			],
		},
	]);

	ngOnInit(): void {
		this.navService.setMenuItems(this.sideMenu);
	}
}
